import { createReducer, on } from '@ngrx/store';
import { GarageActions } from './garage.actions';
import { Mezzo, MezzoQueryParams, MezzoManutenzione, MezzoAssociazione, StatoMezzo } from '../../core/models/garage.model';

export interface GarageState {
  items: Mezzo[];
  loading: boolean;
  error: string | null;

  totalElements: number;
  totalPages: number;
  pageIndex: number;
  pageSize: number;
  lastQuery: MezzoQueryParams | undefined;

  selected: Mezzo | null;
  createSuccessCount: number;

  storicoManutenzioni: MezzoManutenzione[];
  storicoLoading: boolean;
  storicoError: string | null;
  storicoAssociazioni: MezzoAssociazione[];
  storicoAssociazioniLoading: boolean;
  storicoAssociazioniError: string | null;

  opLoading: boolean;
  opError: string | null;
}

export const initialState: GarageState = {
  items: [],
  loading: false,
  error: null,

  totalElements: 0,
  totalPages: 0,
  pageIndex: 0,
  pageSize: 20,
  lastQuery: undefined,

  selected: null,
  createSuccessCount: 0,

  storicoManutenzioni: [],
  storicoLoading: false,
  storicoError: null,
  storicoAssociazioni: [],
  storicoAssociazioniLoading: false,
  storicoAssociazioniError: null,

  opLoading: false,
  opError: null,
};

export const GarageReducer = createReducer(
  initialState,

  // LISTA
  on(GarageActions.loadList, (s, { params, silent }) => ({
    ...s,
    loading: silent ? s.loading : true,
    error: null,
    lastQuery: params,
  })),
  on(GarageActions.loadListSuccess, (s, { items, totalElements, totalPages, pageIndex }) => ({
    ...s,
    loading: false,
    items,
    totalElements,
    totalPages,
    pageIndex,
  })),
  on(GarageActions.loadListNotModified, (s) => ({ ...s, loading: false })),
  on(GarageActions.loadListFailure, (s, { error }) => ({ ...s, loading: false, error })),

  // DETTAGLI
  on(GarageActions.loadDetails, (s) => ({ ...s, opLoading: true, opError: null })),
  on(GarageActions.loadDetailsSuccess, (s, { item }) => ({ ...s, opLoading: false, selected: item })),
  on(GarageActions.loadDetailsFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  // CREATE
  on(GarageActions.create, (s) => ({ ...s, opLoading: true, opError: null })),
  on(GarageActions.createSuccess, (s) => ({ ...s, opLoading: false, createSuccessCount: s.createSuccessCount + 1 })),
  on(GarageActions.createFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  // UPDATE
  on(GarageActions.update, (s) => ({ ...s, opLoading: true, opError: null })),
  on(GarageActions.updateSuccess, (s) => ({ ...s, opLoading: false })),
  on(GarageActions.updateFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  // ASSOCIATE TO CANTIERE
  on(GarageActions.associateToCantiere, (s) => ({ ...s, opLoading: true, opError: null })),
  on(GarageActions.associateToCantiereSuccess, (s, { id, idCantiere }) => ({
    ...s,
    opLoading: false,
    items: s.items.map((item) =>
      item.id === id
        ? {
            ...item,
            idCantiere,
            stato: 'IN_USO' as StatoMezzo,
          }
        : item
    ),
    selected:
      s.selected?.id === id
        ? {
            ...s.selected,
            idCantiere,
            stato: 'IN_USO' as StatoMezzo,
          }
        : s.selected,
  })),
  on(GarageActions.associateToCantiereFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  // END CANTIERE ASSOCIATION
  on(GarageActions.endCantiereAssociation, (s) => ({ ...s, opLoading: true, opError: null })),
  on(GarageActions.endCantiereAssociationSuccess, (s, { id }) => ({
    ...s,
    opLoading: false,
    items: s.items.map((item) =>
      item.id === id
        ? {
            ...item,
            idCantiere: undefined,
            stato: (item.associazione?.idDipendente || item.associazione?.nomeDipendente ? 'IN_USO' : 'In Garage') as StatoMezzo,
          }
        : item
    ),
    selected:
      s.selected?.id === id
        ? {
            ...s.selected,
            idCantiere: undefined,
            stato: (s.selected.associazione?.idDipendente || s.selected.associazione?.nomeDipendente ? 'IN_USO' : 'In Garage') as StatoMezzo,
          }
        : s.selected,
  })),
  on(GarageActions.endCantiereAssociationFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  // ADD MANUTENZIONE
  on(GarageActions.addManutenzione, (s) => ({ ...s, opLoading: true, opError: null })),
  on(GarageActions.addManutenzioneSuccess, (s) => ({ ...s, opLoading: false })),
  on(GarageActions.addManutenzioneFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  // STORICO MANUTENZIONI
  on(GarageActions.loadStoricoManutenzioni, (s) => ({ ...s, storicoLoading: true, storicoError: null })),
  on(GarageActions.loadStoricoManutenzioniSuccess, (s, { items }) => ({
    ...s,
    storicoLoading: false,
    storicoManutenzioni: items,
  })),
  on(GarageActions.loadStoricoManutenzioniFailure, (s, { error }) => ({ ...s, storicoLoading: false, storicoError: error })),

  // STORICO ASSOCIAZIONI
  on(GarageActions.loadStoricoAssociazioni, (s) => ({ ...s, storicoAssociazioniLoading: true, storicoAssociazioniError: null })),
  on(GarageActions.loadStoricoAssociazioniSuccess, (s, { items }) => ({
    ...s,
    storicoAssociazioniLoading: false,
    storicoAssociazioni: items,
  })),
  on(GarageActions.loadStoricoAssociazioniFailure, (s, { error }) => ({ ...s, storicoAssociazioniLoading: false, storicoAssociazioniError: error })),

  on(GarageActions.associateToDipendente, (s) => ({
    ...s,
    opLoading: true,
    opError: null,
  })),

  on(GarageActions.associateToDipendenteSuccess, (s) => ({
    ...s,
    opLoading: false,
  })),

  on(GarageActions.associateToDipendenteFailure, (s, { error }) => ({
    ...s,
    opLoading: false,
    opError: error,
  })),
  on(GarageActions.endDipendenteAssociation, (s) => ({
    ...s,
    opLoading: true,
    opError: null,
  })),

  on(GarageActions.endDipendenteAssociationSuccess, (s, { id }) => ({
    ...s,
    opLoading: false,
    items: s.items.map((item) =>
      item.id === id
        ? {
          ...item,
          associazione: item.associazione
            ? {
              ...item.associazione,
              idDipendente: undefined,
              nomeDipendente: undefined,
              contattoDipendente: undefined,
            }
            : item.associazione,
          stato: item.idCantiere ? 'IN_USO' as StatoMezzo : 'In Garage' as StatoMezzo,
        }
        : item
    ),
    selected:
      s.selected?.id === id
        ? {
          ...s.selected,
          associazione: s.selected.associazione
            ? {
              ...s.selected.associazione,
              idDipendente: undefined,
              nomeDipendente: undefined,
              contattoDipendente: undefined,
            }
            : s.selected.associazione,
          stato: s.selected.idCantiere ? 'IN_USO' as StatoMezzo : 'In Garage' as StatoMezzo,
        }
        : s.selected,
  })),

  on(GarageActions.endDipendenteAssociationFailure, (s, { error }) => ({
    ...s,
    opLoading: false,
    opError: error,
  })),

  on(GarageActions.setUsability, (s) => ({
    ...s,
    opLoading: true,
    opError: null,
  })),

  on(GarageActions.setUsabilitySuccess, (s, { id, stato }) => ({
    ...s,
    opLoading: false,
    items: s.items.map(item =>
      item.id === id ? { ...item, stato } : item
    ),
    selected:
      s.selected?.id === id
        ? { ...s.selected, stato }
        : s.selected,
  })),

  on(GarageActions.setUsabilityFailure, (s, { error }) => ({
    ...s,
    opLoading: false,
    opError: error,
  })),
);
